import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    UserDefinedObjectTemplateAdditionalColumnType,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {
    type OttopilotEventContainerConstraint,
    ottopilotEventContainerConstraintValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerConstraint";
import {
    type OttopilotDispositionConfig,
    OttopilotDispositionEmitWhen,
    type OttopilotDispositionOptionRule,
    OttopilotDispositionOptionRuleActionKind,
    OttopilotDispositionOptionRuleCountMode,
    OttopilotDispositionPluginKind,
    OttopilotSuggestedJourneyStatusRuleKind,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotEventContainerTableViewColumn} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTableViewColumn";
import {
    OttopilotScheduleValueEncoding,
    OttopilotTrigger,
    type OttopilotTriggerConfig,
    OttopilotTriggerDataDriver,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import {
    type OttopilotEventArchiveConfig,
    OttopilotEventArchiveRuleKind,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventArchiveConfig";
import type {
    KanbanBoardColumnConfig,
    KanbanCardSystemFieldId,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {
    kanbanBoardColumnConfigValidator,
    kanbanCardSystemFieldIdValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {
    type AbacPolicy,
    abacPolicyValidator,
} from "@vesta/vos-types/modules/workspaceLevel/ottopilotAuthorization/domain/AbacPolicy";
import {OttopilotColumnType} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotColumn";
import {
    type OttopilotDataViewerConfig,
    ottopilotDataViewerConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {
    type SharedCalendarViewConfig,
    sharedCalendarViewConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {
    type OttopilotDispositionColumnConfig,
    ottopilotDispositionColumnConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDispositionColumnConfig";
import {
    type OttopilotOrganizationalUnitColumnConfig,
    ottopilotOrganizationalUnitColumnConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotOrganizationalUnitColumnConfig";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export enum OttopilotEventContainerViewPanelSectionKind {
    columnGroup = "5b3aede8-bc58-47f3-ad7f-98b2cfe7bf38",
    eventContainerOverview = "438dcd2b-80af-4d31-ad89-86198b34297c",
}

export type OttopilotEventContainerTemplateConfiguration = {
    /** Physical JSON UDO column storing notes; exposed as the existing notes field by the capability API. */
    notesColumnId: Uuid;
    statusColumnId: Uuid;
    accessControlPolicies: Array<AbacPolicy>;
    displayNameTemplate: string;
    dispositionColumnConfig: OttopilotDispositionColumnConfig;
    organizationalUnitColumnConfig: OttopilotOrganizationalUnitColumnConfig;
    dispositionConfig?: OttopilotDispositionConfig;
    triggerConfig?: OttopilotTriggerConfig;
    dedupeLogic?: {
        id: Uuid;
        fields: Array<Uuid>;
        strategyId: Uuid;
    };
    /**
     * Configuration for the additional column that represents the status of the Event Container.
     * Used for archiving Event Containers by updating this column to the archive status value.
     * When defaultStatusValue is set, new Event Containers
     * will have this status value set on the status column (e.g. "active" / "live").
     */
    statusColumnConfig?: {
        columnId: Uuid;
        archiveStatusValue: Uuid;
        /** Status value (option ID) to set when creating a new Event Container. */
        defaultStatusValue?: Uuid;
    };
    /**
     * Optional guards for Event Container archiving, evaluated against Event Container additional-column values.
     */
    archiveConfig?: OttopilotEventArchiveConfig;
    validOrganizationUnitTemplateIds: Array<Uuid>;
    /** When set, backend normalizes legacy product_catalogues FKs to this template's item table. */
    defaultProductCatalogueTemplateId?: Uuid;
    /** Optional: restrict Event Container product columns to templates in this list. */
    validProductCatalogueTemplateIds?: Array<Uuid>;
    /**
     * Defines what this module is shown as on the frontend.
     * It may be presented using a workspace-specific name such as Lead, Opportunity, or Service Case.
     */
    moduleDisplayNameVariants: {
        singularSentenceCase: string;
        pluralSentenceCase: string;
        singularLowerCase: string;
        pluralLowerCase: string;
    };
    eventContainersPage: {
        ottopilotDataViewerConfig?: OttopilotDataViewerConfig;
        tableViewColumns?: Array<OttopilotEventContainerTableViewColumn>;
        editPanel?: {
            columnGrouping: Array<{
                id: Uuid;
                displayName: string;
                iconId?: Uuid;
                iconColor?: string;
                iconBackgroundColor?: string;
                initialIsOpen?: boolean;
                className?: string;
                columns: Array<
                    {
                        id: Uuid;
                        isEditable: boolean;
                        label: string;
                        columnId: Uuid | string;
                        constraints?: Array<OttopilotEventContainerConstraint>;
                        isAdditionalColumn: boolean;
                    } & (
                        | {
                              columnType: "OttopilotEvent";
                              eventTemplateId: Uuid;
                          }
                        | {
                              columnType: "OttopilotEventContainer";
                              eventTemplateId?: never;
                          }
                    )
                >;
            }>;
            globalConstraints?: Array<OttopilotEventContainerConstraint>;
        };
        viewPanel?: {
            sections: Array<
                | {
                      kind: OttopilotEventContainerViewPanelSectionKind.columnGroup;
                      id: Uuid;
                      displayName: string;
                      iconId?: Uuid;
                      iconColor?: string;
                      iconBackgroundColor?: string;
                      initialIsOpen?: boolean;
                      className?: string;
                      columns: Array<
                          {
                              id: Uuid;
                              label: string;
                              columnId: Uuid | string;
                              isAdditionalColumn: boolean;
                          } & ({columnType: "OttopilotEvent"; eventTemplateId: Uuid} | {columnType: "OttopilotEventContainer"; eventTemplateId?: never})
                      >;
                  }
                | {
                      kind: OttopilotEventContainerViewPanelSectionKind.eventContainerOverview;
                      id: Uuid;
                      displayName?: string;
                      iconId?: Uuid;
                      iconColor?: string;
                      iconBackgroundColor?: string;
                      initialIsOpen?: boolean;
                      className?: string;
                  }
            >;
        };
        kanbanBoardCardView: {
            heading: Array<Uuid | KanbanCardSystemFieldId>;
            details: Array<
                Array<
                    {
                        iconId: Uuid;
                        label: string;
                    } & (
                        | {
                              type: "SingleColumn";
                              dataSource: Uuid | KanbanCardSystemFieldId;
                          }
                        | {
                              type: "MultipleColumn";
                              seperator: string;
                              dataSource: Array<Uuid | KanbanCardSystemFieldId>;
                          }
                    )
                >
            >;
        };
        kanbanBoardPageView: {
            filterFieldId: Uuid | string;
            columnConfig: Array<KanbanBoardColumnConfig>;
        };
        /**
         * Shared shape for an Event Container queue calendar.
         * When `eventTypeSource`, `calendarStartDateColumnId`, and `calendarTitleOttopilotStringTemplate`
         * are set, the calendar uses Event Container values for colors, placement, and titles.
         */
        inQueueCalendarConfig?: SharedCalendarViewConfig & {
            eventTypeSource?: {
                columnType: "OttopilotEventContainer";
                columnId: Uuid | "status";
            };
            /** Event Container additional column holding an ISO 8601 datetime for calendar placement. */
            calendarStartDateColumnId?: Uuid;
            /** Interpolate the card title from Event Container additional-column values. */
            calendarTitleOttopilotStringTemplate?: string;
        };
        /**
         * Calendar configuration for the main Event Containers page, distinct from {@link inQueueCalendarConfig}.
         */
        eventContainerCalendarConfig?: SharedCalendarViewConfig & {
            eventTypeSource?: {
                columnType: "OttopilotEventContainer";
                columnId: Uuid | "status";
            };
            calendarStartDateColumnId?: Uuid;
            calendarTitleOttopilotStringTemplate?: string;
        };
        /**
         * ID of the additionalColumn that should be treated at the primary phone number to display the call
         * button. A value of null indicates a call button should not be drawn.
         */
        callButtonColumnId: Uuid | null;
        whatsAppButtonColumnId: Uuid | null;
        /**
         * When set, a "Create" button is shown on the Event Containers index page.
         */
        writeConfig?: {
            formId: Uuid;
        };
        /**
         * ID of the additionalColumn that should be treated as the lead owner.
         */
        leadOwnerColumnId?: Uuid;
    };
    eventContainerEventsPage: {
        tableView?: {
            columns: Array<{
                id: Uuid;
                label: string;
                type: OttopilotColumnType;
                valueGetterId: Uuid;
                valueFormatterId?: Uuid;
            }>;
            actionButton?: Array<{
                id: Uuid;
                displayName: string;
            }>;
        };
        EventColorMapping: Record<
            Uuid,
            {
                textColor: string;
                background: string;
            }
        >;
    };
    eventContainerDetailsPage: {
        remarksColumnId: Uuid | null;
        columnGrouping: Array<{
            id: Uuid;
            displayName: string;
            columns: Array<
                {
                    id: Uuid;
                    isEditable: boolean;
                    label: string;
                    columnId: Uuid | string;
                    isAdditionalColumn: boolean;
                } & (
                    | {
                          columnType: "OttopilotEvent";
                          eventTemplateId: Uuid;
                      }
                    | {
                          columnType: "OttopilotEventContainer";
                          eventTemplateId?: never;
                      }
                )
            >;
        }>;
    };
};

export type OttopilotEventContainersPageConfiguration =
    OttopilotEventContainerTemplateConfiguration["eventContainersPage"];

export type OttopilotEventContainerEventsPageConfiguration =
    OttopilotEventContainerTemplateConfiguration["eventContainerEventsPage"];

export type OttopilotEventContainerDetailsPageConfiguration =
    OttopilotEventContainerTemplateConfiguration["eventContainerDetailsPage"];

const dispositionOptionRuleValidator: zod.ZodType<OttopilotDispositionOptionRule> = zod.strictObject({
    id: uuidZodValidator,
    when: zod.strictObject({
        level: zod.number(),
        selectedPrefix: zod.string().array(),
    }),
    targetSegment: zod.string(),
    countDispositionId: uuidZodValidator,
    countMode: zod.enum(OttopilotDispositionOptionRuleCountMode),
    action: zod.discriminatedUnion("kind", [
        zod.strictObject({
            kind: zod.literal(OttopilotDispositionOptionRuleActionKind.hideWhenCountGte),
            max: zod.number(),
        }),
        zod.strictObject({
            kind: zod.literal(OttopilotDispositionOptionRuleActionKind.setLabelWhenCountLt),
            max: zod.number(),
            labelTemplate: zod.string(),
        }),
        zod.strictObject({
            kind: zod.literal(OttopilotDispositionOptionRuleActionKind.hideWhenHistoryContainsNonAllowedDispositionId),
            allowedDispositionIds: uuidZodValidator.array(),
        }),
        zod.strictObject({
            kind: zod.literal(OttopilotDispositionOptionRuleActionKind.hideWhenCurrentDispositionIdEquals),
        }),
    ]),
});

const suggestedJourneyStatusRuleValidator = zod.discriminatedUnion("kind", [
    zod.strictObject({
        kind: zod.literal(OttopilotSuggestedJourneyStatusRuleKind.dispositionIdInSet),
        dispositionIds: uuidZodValidator.array(),
        suggested: uuidZodValidator,
    }),
    zod.strictObject({
        kind: zod.literal(OttopilotSuggestedJourneyStatusRuleKind.dispositionIdWithConsecutiveHistory),
        triggerDispositionId: uuidZodValidator,
        countDispositionId: uuidZodValidator,
        consecutiveMin: zod.number(),
        suggested: uuidZodValidator,
    }),
    zod.strictObject({
        kind: zod.literal(OttopilotSuggestedJourneyStatusRuleKind.dispositionDateWithin),
        triggerDispositionId: uuidZodValidator,
        timeFrom: zod.number(),
        suggested: uuidZodValidator,
    }),
]);

const dispositionConfigValidator: zod.ZodType<OttopilotDispositionConfig> = zod.strictObject({
    plugins: zod
        .discriminatedUnion("kind", [
            zod.strictObject({
                kind: zod.literal(OttopilotDispositionPluginKind.dispositionHistory),
                lastN: zod.number(),
            }),
            zod.strictObject({
                kind: zod.literal(OttopilotDispositionPluginKind.optionRules),
                rules: dispositionOptionRuleValidator.array(),
            }),
            zod.strictObject({
                kind: zod.literal(OttopilotDispositionPluginKind.suggestedJourneyStatusRules),
                dispositionStatusColumnId: uuidZodValidator,
                rules: suggestedJourneyStatusRuleValidator.array(),
            }),
        ])
        .array(),
    emitWhen: zod.enum(OttopilotDispositionEmitWhen),
});

const triggerDataColumnValidator = zod.discriminatedUnion("driver", [
    zod.strictObject({
        columnId: uuidZodValidator,
        columnType: zod.enum(["OttopilotEventContainer", "OttopilotEvent"]),
        driver: zod.literal(OttopilotTriggerDataDriver.staticDefault),
        defaultValue: zod.unknown().optional(),
    }),
    zod.strictObject({
        columnId: uuidZodValidator,
        columnType: zod.enum(["OttopilotEventContainer", "OttopilotEvent"]),
        driver: zod.literal(OttopilotTriggerDataDriver.workspaceUser),
    }),
    zod.strictObject({
        columnId: uuidZodValidator,
        columnType: zod.enum(["OttopilotEventContainer", "OttopilotEvent"]),
        driver: zod.literal(OttopilotTriggerDataDriver.workspaceUserTemplateId),
    }),
    zod.strictObject({
        columnId: uuidZodValidator,
        columnType: zod.enum(["OttopilotEventContainer", "OttopilotEvent"]),
        driver: zod.literal(OttopilotTriggerDataDriver.currentTimestamp),
    }),
    zod.strictObject({
        columnId: uuidZodValidator,
        columnType: zod.enum(["OttopilotEventContainer", "OttopilotEvent"]),
        driver: zod.literal(OttopilotTriggerDataDriver.eventContainerDisposition),
    }),
    zod.strictObject({
        columnId: uuidZodValidator,
        columnType: zod.enum(["OttopilotEventContainer", "OttopilotEvent"]),
        driver: zod.literal(OttopilotTriggerDataDriver.runTimeDefault),
        referenceField: uuidZodValidator,
        referenceFieldType: zod.enum(["OttopilotEventContainer", "OttopilotEvent"]),
    }),
]);

const triggerConfigValidator: zod.ZodType<OttopilotTriggerConfig> = zod
    .discriminatedUnion("type", [
        zod.strictObject({
            eventTemplateId: uuidZodValidator,
            triggerDispositions: uuidZodValidator.array(),
            dataColumns: triggerDataColumnValidator.array(),
            type: zod.literal(OttopilotTrigger.dateTimeTrigger),
            dateTimeColumnId: uuidZodValidator,
            scheduleFieldLabel: zod.string().optional(),
            scheduleValueEncoding: zod.enum(OttopilotScheduleValueEncoding),
            syncSelectedDispositionToColumnIds: uuidZodValidator.array().optional(),
        }),
        zod.strictObject({
            eventTemplateId: uuidZodValidator,
            triggerDispositions: uuidZodValidator.array(),
            dataColumns: triggerDataColumnValidator.array(),
            type: zod.literal(OttopilotTrigger.noComponentTrigger),
        }),
    ])
    .array();

const ottopilotEventArchiveConfigValidator: zod.ZodType<OttopilotEventArchiveConfig> = zod.strictObject({
    rules: zod
        .discriminatedUnion("kind", [
            zod.strictObject({
                kind: zod.literal(OttopilotEventArchiveRuleKind.requiresEmptyColumn),
                columnId: uuidZodValidator,
                disabledReason: zod.string(),
            }),
            zod.strictObject({
                kind: zod.literal(OttopilotEventArchiveRuleKind.requiresEqualsColumnValue),
                columnId: uuidZodValidator,
                expectedValue: zod.unknown(),
                disabledReason: zod.string(),
            }),
            zod.strictObject({
                kind: zod.literal(OttopilotEventArchiveRuleKind.requiresNotEqualsColumnValue),
                columnId: uuidZodValidator,
                expectedValue: zod.unknown(),
                disabledReason: zod.string(),
            }),
        ])
        .array(),
});

const eventContainerTableViewColumnBaseValidator = zod.strictObject({
    id: uuidZodValidator,
    field: zod.string(),
    displayName: zod.string(),
    sortingCapability: zod.boolean(),
    filteringCapability_contains: zod.boolean(),
});

const eventContainerTableViewColumnValidator: zod.ZodType<OttopilotEventContainerTableViewColumn> = zod.union([
    eventContainerTableViewColumnBaseValidator.extend({
        isAdditionalColumn: zod.literal(true),
        columnType: zod.literal("OttopilotEventContainer"),
        type: zod.enum(UserDefinedObjectTemplateAdditionalColumnType),
    }),
    eventContainerTableViewColumnBaseValidator.extend({
        isAdditionalColumn: zod.literal(true),
        columnType: zod.literal("OttopilotEvent"),
        type: zod.enum(UserDefinedObjectTemplateAdditionalColumnType),
        eventTemplateId: uuidZodValidator,
    }),
    eventContainerTableViewColumnBaseValidator.extend({
        isAdditionalColumn: zod.literal(false),
        columnType: zod.literal("OttopilotEventContainer"),
    }),
    eventContainerTableViewColumnBaseValidator.extend({
        isAdditionalColumn: zod.literal(false),
        columnType: zod.literal("OttopilotEvent"),
        eventTemplateId: uuidZodValidator,
    }),
]);

const editableColumnValidator = zod.union([
    zod.strictObject({
        id: uuidZodValidator,
        isEditable: zod.boolean(),
        label: zod.string(),
        columnId: zod.string(),
        constraints: ottopilotEventContainerConstraintValidator.array().optional(),
        isAdditionalColumn: zod.boolean(),
        columnType: zod.literal("OttopilotEvent"),
        eventTemplateId: uuidZodValidator,
    }),
    zod.strictObject({
        id: uuidZodValidator,
        isEditable: zod.boolean(),
        label: zod.string(),
        columnId: zod.string(),
        constraints: ottopilotEventContainerConstraintValidator.array().optional(),
        isAdditionalColumn: zod.boolean(),
        columnType: zod.literal("OttopilotEventContainer"),
    }),
]);

const readOnlyColumnValidator = zod.union([
    zod.strictObject({
        id: uuidZodValidator,
        label: zod.string(),
        columnId: zod.string(),
        isAdditionalColumn: zod.boolean(),
        columnType: zod.literal("OttopilotEvent"),
        eventTemplateId: uuidZodValidator,
    }),
    zod.strictObject({
        id: uuidZodValidator,
        label: zod.string(),
        columnId: zod.string(),
        isAdditionalColumn: zod.boolean(),
        columnType: zod.literal("OttopilotEventContainer"),
    }),
]);

const sectionPresentationValidator = {
    id: uuidZodValidator,
    displayName: zod.string(),
    iconId: uuidZodValidator.optional(),
    iconColor: zod.string().optional(),
    iconBackgroundColor: zod.string().optional(),
    initialIsOpen: zod.boolean().optional(),
    className: zod.string().optional(),
};

const kanbanCardDataSourceValidator = zod.union([uuidZodValidator, kanbanCardSystemFieldIdValidator]);

const kanbanBoardCardViewValidator = zod.strictObject({
    heading: kanbanCardDataSourceValidator.array(),
    details: zod.array(
        zod.array(
            zod.discriminatedUnion("type", [
                zod.strictObject({
                    iconId: uuidZodValidator,
                    label: zod.string(),
                    type: zod.literal("SingleColumn"),
                    dataSource: kanbanCardDataSourceValidator,
                }),
                zod.strictObject({
                    iconId: uuidZodValidator,
                    label: zod.string(),
                    type: zod.literal("MultipleColumn"),
                    seperator: zod.string(),
                    dataSource: kanbanCardDataSourceValidator.array(),
                }),
            ]),
        ),
    ),
});

const eventContainerCalendarConfigValidator = sharedCalendarViewConfigValidator.extend({
    eventTypeSource: zod
        .strictObject({
            columnType: zod.literal("OttopilotEventContainer"),
            columnId: zod.union([uuidZodValidator, zod.literal("status")]),
        })
        .optional(),
    calendarStartDateColumnId: uuidZodValidator.optional(),
    calendarTitleOttopilotStringTemplate: zod.string().optional(),
});

export const ottopilotEventContainerTemplateConfigurationValidator: zod.ZodType<OttopilotEventContainerTemplateConfiguration> = zod.strictObject({
    notesColumnId: uuidZodValidator,
    statusColumnId: uuidZodValidator,
    accessControlPolicies: abacPolicyValidator.array(),
    displayNameTemplate: zod.string(),
    dispositionColumnConfig: ottopilotDispositionColumnConfigValidator,
    organizationalUnitColumnConfig: ottopilotOrganizationalUnitColumnConfigValidator,
    dispositionConfig: dispositionConfigValidator.optional(),
    triggerConfig: triggerConfigValidator.optional(),
    dedupeLogic: zod
        .strictObject({
            id: uuidZodValidator,
            fields: uuidZodValidator.array(),
            strategyId: uuidZodValidator,
        })
        .optional(),
    statusColumnConfig: zod
        .strictObject({
            columnId: uuidZodValidator,
            archiveStatusValue: uuidZodValidator,
            defaultStatusValue: uuidZodValidator.optional(),
        })
        .optional(),
    archiveConfig: ottopilotEventArchiveConfigValidator.optional(),
    validOrganizationUnitTemplateIds: uuidZodValidator.array(),
    defaultProductCatalogueTemplateId: uuidZodValidator.optional(),
    validProductCatalogueTemplateIds: uuidZodValidator.array().optional(),
    moduleDisplayNameVariants: zod.strictObject({
        singularSentenceCase: zod.string(),
        pluralSentenceCase: zod.string(),
        singularLowerCase: zod.string(),
        pluralLowerCase: zod.string(),
    }),
    eventContainersPage: zod.strictObject({
        ottopilotDataViewerConfig: ottopilotDataViewerConfigValidator.optional(),
        tableViewColumns: eventContainerTableViewColumnValidator.array().optional(),
        editPanel: zod
            .strictObject({
                columnGrouping: zod
                    .strictObject({
                        ...sectionPresentationValidator,
                        columns: editableColumnValidator.array(),
                    })
                    .array(),
                globalConstraints: ottopilotEventContainerConstraintValidator.array().optional(),
            })
            .optional(),
        viewPanel: zod
            .strictObject({
                sections: zod
                    .union([
                        zod.strictObject({
                            ...sectionPresentationValidator,
                            kind: zod.literal(OttopilotEventContainerViewPanelSectionKind.columnGroup),
                            columns: readOnlyColumnValidator.array(),
                        }),
                        zod.strictObject({
                            id: uuidZodValidator,
                            kind: zod.literal(OttopilotEventContainerViewPanelSectionKind.eventContainerOverview),
                            displayName: zod.string().optional(),
                            iconId: uuidZodValidator.optional(),
                            iconColor: zod.string().optional(),
                            iconBackgroundColor: zod.string().optional(),
                            initialIsOpen: zod.boolean().optional(),
                            className: zod.string().optional(),
                        }),
                    ])
                    .array(),
            })
            .optional(),
        kanbanBoardCardView: kanbanBoardCardViewValidator,
        kanbanBoardPageView: zod.strictObject({
            filterFieldId: zod.string(),
            columnConfig: kanbanBoardColumnConfigValidator.array(),
        }),
        inQueueCalendarConfig: eventContainerCalendarConfigValidator.optional(),
        eventContainerCalendarConfig: eventContainerCalendarConfigValidator.optional(),
        callButtonColumnId: uuidZodValidator.nullable(),
        whatsAppButtonColumnId: uuidZodValidator.nullable(),
        writeConfig: zod
            .strictObject({
                formId: uuidZodValidator,
            })
            .optional(),
        leadOwnerColumnId: uuidZodValidator.optional(),
    }),
    eventContainerEventsPage: zod.strictObject({
        tableView: zod
            .strictObject({
                columns: zod
                    .strictObject({
                        id: uuidZodValidator,
                        label: zod.string(),
                        type: zod.enum(OttopilotColumnType),
                        valueGetterId: uuidZodValidator,
                        valueFormatterId: uuidZodValidator.optional(),
                    })
                    .array(),
                actionButton: zod
                    .strictObject({
                        id: uuidZodValidator,
                        displayName: zod.string(),
                    })
                    .array()
                    .optional(),
            })
            .optional(),
        EventColorMapping: zod.record(
            uuidZodValidator,
            zod.strictObject({
                textColor: zod.string(),
                background: zod.string(),
            }),
        ),
    }),
    eventContainerDetailsPage: zod.strictObject({
        remarksColumnId: uuidZodValidator.nullable(),
        columnGrouping: zod
            .strictObject({
                id: uuidZodValidator,
                displayName: zod.string(),
                columns: editableColumnValidator.array(),
            })
            .array(),
    }),
});
